import { useSlate } from "slate-react";
import type { CustomElement, CustomText, ParagraphElement } from "@/interfaces/CampaignBuilder";
import type { TextAlign, TextFormat } from "@/types/types";
import { isAlignActive, isMarkActive, setAlign, toggleMark } from "./html-helper";

export function AlignButton({ align, children }: { align: TextAlign; children: React.ReactNode }) {
  const editor = useSlate();
  const active = isAlignActive(editor, align);
  return (
    <button
      type="button"
      onMouseDown={(e) => {
        e.preventDefault();
        setAlign(editor, align);
      }}
      className={[
        "w-7 h-7 flex items-center justify-center rounded transition-colors",
        active ? "bg-rodeo-black text-white" : "text-graphite hover:bg-smoke",
      ].join(" ")}
      aria-pressed={active}
      title={`Align ${align}`}
    >
      {children}
    </button>
  );
}

export function MarkButton({
  format,
  children,
}: {
  format: TextFormat;
  children: React.ReactNode;
}) {
  const editor = useSlate();
  const active = isMarkActive(editor, format);
  return (
    <button
      type="button"
      onMouseDown={(e) => {
        e.preventDefault();
        toggleMark(editor, format);
      }}
      className={[
        "w-7 h-7 flex items-center justify-center rounded transition-colors",
        active ? "bg-rodeo-black text-white" : "text-graphite hover:bg-smoke",
      ].join(" ")}
      aria-pressed={active}
      title={format.charAt(0).toUpperCase() + format.slice(1)}
    >
      {children}
    </button>
  );
}

export function Leaf({
  attributes,
  children,
  leaf,
}: {
  attributes: object;
  children: React.ReactNode;
  leaf: CustomText;
}) {
  let node = children;
  if (leaf.bold) node = <strong>{node}</strong>;
  if (leaf.italic) node = <em>{node}</em>;
  if (leaf.underline) node = <u>{node}</u>;
  return <span {...attributes}>{node}</span>;
}

export function Element({
  attributes,
  children,
  element,
}: {
  attributes: object;
  children: React.ReactNode;
  element: CustomElement;
}) {
  return (
    <p {...attributes} style={{ textAlign: (element as ParagraphElement).align ?? "left" }}>
      {children}
    </p>
  );
}
